import { useState, useCallback, useEffect } from "react";

import { AccessTokenError, isOnboardingAllowedForCountry } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { ENVIRONMENTS_BOOLEANS } from "../../../common/constants/env";
import { useCurrentUserContext } from "../../../common/contexts/CurrentUserContext";
import { useStripeAccountContext } from "../../../common/contexts/StripeAccountContext";
import { trpc } from "../../../common/helpers/trpc";
import { useTranslate } from "../../../common/hooks/useTranslate";

import { isErrorWithCode } from "@/common/helpers/error";
import { DashboardButtonProps } from "@/dashboard-design-system/DashboardButton";

export type BankOnboardingButtonProps = DashboardButtonProps & {
  isCustomElement?: boolean;
  disabled?: boolean;
};

export const useRedirectToStripeButton = () => {
  const { refetchOrganization } = useStripeAccountContext();
  const { organization } = useCurrentUserContext();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const { getStripeAccountLink } = trpc.useUtils();

  // When org signup, the stripe account is created in async. We need to refetch the organization be sure the following redirectToStripe function
  // does not call createStripeAccountWithLink if account has already a stripeCustomAccountId
  useEffect(() => {
    refetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRedirectionLink = useCallback(async () => {
    if (!isOnboardingAllowedForCountry(organization?.country, ENVIRONMENTS_BOOLEANS)) {
      enqueueSnackbar("Stripe onboarding is not currently supported for organizations in Australia and New Zealand", {
        vibe: "danger",
      });
      return null;
    }

    setIsLoading(true);
    try {
      return await getStripeAccountLink.fetch();
    } catch (error) {
      if (isErrorWithCode(error) && error.code === AccessTokenError.UrlInvalid) {
        enqueueSnackbar(t("dashboard", "invalidDomainUrl"), { vibe: "danger" });
      } else if (isErrorWithCode(error) && error.code === "stripe_onboarding_not_supported") {
        enqueueSnackbar("Stripe onboarding is not currently supported for organizations in Australia and New Zealand", {
          vibe: "danger",
        });
      } else {
        enqueueSnackbar(t("common", "unknownError"), { vibe: "danger" });
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  }, [getStripeAccountLink, enqueueSnackbar, t, organization?.country]);

  return { getRedirectionLink, isLoading, setIsLoading };
};
